/* [PATCH opombe-stranka-fe v1] Stranka: postavka »Za urediti« v meniju (znacka s stevilom)
   + okno ob vstopu (enkrat na sejo za par podjetje x aplikacija).
   Nacrt: claude/NACRT_opombe_za_stranko_v2_20260930.md (odlocitvi 1 in 4).
   Samo vloga stranka, samo aplikaciji Nabava in Prodaja; seznam iz GET /stranka/{cid}/opombe/odprte
   (backend ze filtrira po aplikacijah uporabnika v podjetju, tu se po aktivni aplikaciji).
   Odgovor je mogoc NEPOSREDNO v oknu — stranka v Prodaji maske izdanega racuna ne vidi
   (DokumentiPage jo preusmeri na pogled=rocni). Nabava: tudi »Odpri račun«. */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, X } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { useAplikacija } from '../../hooks/useAplikacija';
import { OpombeNit, OPOMBE_DOGODEK } from './OpombeNit';

const fmtDatum = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const p = (x: number) => String(x).padStart(2, '0');
  return p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + d.getFullYear();
};

const mnozRac = (n: number) => {
  const m = n % 100;
  return m === 1 ? 'račun' : m === 2 ? 'računa' : (m === 3 || m === 4) ? 'račune' : 'računov';
};

export function OpombeNavPostavka({ sidebarOpen }: { sidebarOpen: boolean }) {
  const role = useAuthStore((s: any) => s.role);
  const company = useAuthStore((s: any) => s.selectedCompany);
  const { aplikacija } = useAplikacija();
  const [seznam, setSeznam] = useState<any[]>([]);
  const [odprto, setOdprto] = useState(false);
  const [razsirjen, setRazsirjen] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const nalozenKljuc = useRef<string>('');

  const cid: string | undefined = company?.id;
  const app = (aplikacija === 'nabava' || aplikacija === 'prodaja') ? aplikacija : null;
  const aktivno = role === 'stranka' && !!cid && !!app;
  const kljucSeje = `opombe_okno_${cid}_${app}`;

  const nalozi = async () => {
    if (!aktivno) { setSeznam([]); return; }
    try {
      const r = await api.get(`/stranka/${cid}/opombe/odprte`);
      if (useAuthStore.getState().selectedCompany?.id !== cid) return;
      const s = (r.data || []).filter((x: any) => x.aplikacija === app);
      setSeznam(s);
      const kljuc = `${cid}_${app}`;
      if (nalozenKljuc.current !== kljuc) {           // prvo nalaganje za ta par -> okno ob vstopu
        nalozenKljuc.current = kljuc;
        let ze = false;
        try { ze = sessionStorage.getItem(kljucSeje) === '1'; } catch { /* */ }
        if (s.length > 0 && !ze) {
          setOdprto(true);
          try { sessionStorage.setItem(kljucSeje, '1'); } catch { /* */ }
        }
      }
    } catch { /* 403/404 -> tiho */ }
  };

  useEffect(() => { nalozi(); /* eslint-disable-next-line */ }, [aktivno, cid, app, location.pathname]);
  useEffect(() => {
    if (!aktivno) return;
    const t = setInterval(() => { if (document.visibilityState === 'visible') nalozi(); }, 10000); /* [PATCH opombe-osvezevanje v1] */
    const ob = () => nalozi();
    window.addEventListener(OPOMBE_DOGODEK, ob);
    return () => { clearInterval(t); window.removeEventListener(OPOMBE_DOGODEK, ob); };
    // eslint-disable-next-line
  }, [aktivno, cid, app]);

  if (!aktivno) return null;
  const n = seznam.length;

  const odpriRacun = (x: any) => {
    setOdprto(false);
    navigate(`/dokumenti?tip=prejeti_racun&opomba_doc=${x.document_id}&ts=${Date.now()}`);
  };

  const okno = odprto && createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={() => setOdprto(false)}>
      <div className="w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-amber-700 dark:text-amber-300" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-gray-900 dark:text-white">
              {n > 0 ? `Računovodstvo vas prosi, da uredite ${n} ${mnozRac(n)}` : 'Ni odprtih opomb'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{company?.name || ''} · {app === 'nabava' ? 'Nabava' : 'Prodaja'}</div>
          </div>
          <button type="button" onClick={() => setOdprto(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto px-5 py-4 space-y-3">
          {seznam.map((x) => (
            <div key={x.id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {x.partner || '(brez partnerja)'}{x.stevilka ? ' · ' + x.stevilka : ''}{x.datum ? ' · ' + fmtDatum(x.datum) : ''}
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap line-clamp-3 mt-0.5">{x.besedilo}</div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button type="button" onClick={() => setRazsirjen(razsirjen === x.id ? null : x.id)}
                    className="text-xs px-2.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium">
                    {razsirjen === x.id ? 'Zapri' : 'Odgovori'}
                  </button>
                  {app === 'nabava' && (
                    <button type="button" onClick={() => odpriRacun(x)}
                      className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200">
                      Odpri račun
                    </button>
                  )}
                </div>
              </div>
              {razsirjen === x.id && cid && (
                <div className="mt-3"><OpombeNit companyId={cid} docId={x.document_id} jeStranka={true} kompaktno /></div>
              )}
            </div>
          ))}
          {n === 0 && <div className="text-sm text-gray-500 dark:text-gray-400">Vse opombe so urejene. Hvala!</div>}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end">
          <button type="button" onClick={() => setOdprto(false)}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800">Kasneje</button>
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      {n > 0 && (
        <button type="button" onClick={() => { setRazsirjen(null); setOdprto(true); }}
          title="Računi, ki jih mora urediti stranka"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-900/40">
          <span className="relative">
            <MessageSquare className="w-5 h-5 flex-shrink-0" />
            {!sidebarOpen && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500" />}
          </span>
          {sidebarOpen && (
            <>
              <span>Za urediti</span>
              <span className="ml-auto text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full">{n}</span>
            </>
          )}
        </button>
      )}
      {okno}
    </>
  );
}
